/**
 * 🔔 Professional Toast Notifications System for Main-Bet
 * 
 * Features:
 * - Smart queue system (no overlapping toasts)
 * - Priority-based notifications
 * - User preference tracking
 * - Beautiful minimalist design
 * - Real-time odds tracking
 * - Engagement optimization
 * - Manual type controls for development
 */

import { pickTopMatch } from './topMatchScoring';
// Конфигурация системы
const CONFIG = {
  SESSION_RESET_HOURS: 2,
  TOP_MATCH_DELAY_MS: 15000,
  QUEUE_PROCESS_DELAY_MS: 1000,
  LOCAL_STORAGE_KEYS: {
    PREFERENCES: 'mainbet_toast_preferences'
  },
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development'
};

// Безопасная работа с localStorage
const safeLocalStorage = {
  getItem: (key) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      if (CONFIG.IS_DEVELOPMENT) console.warn('localStorage getItem failed:', error);
    }
    return null;
  },
  
  setItem: (key, value) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      if (CONFIG.IS_DEVELOPMENT) console.warn('localStorage setItem failed:', error);
    }
    return false;
  }
};

// Типы уведомлений с приоритетами (чем выше число, тем важнее)
// 💡 Для отключения конкретного типа: установите enabled: false
export const TOAST_TYPES = {
  TOP_MATCH: {
    id: 'top_match',
    enabled: true, // 🔥 Установите false для отключения топ-матча
    priority: 3,
    duration: 8000,
    maxPerSession: 1,
    delay: CONFIG.TOP_MATCH_DELAY_MS,
    color: 'primary'
  },
  ODDS_CHANGE: {
    id: 'odds_change',
    enabled: true, // 📈 Установите false для отключения изменений коэффициентов
    priority: 4,
    duration: 6000,
    maxPerSession: 2,
    delay: 0,
    color: 'success'
  },
  ACCUMULATOR_PROMPT: {
    id: 'accumulator_prompt',
    enabled: true, // 💰 Установите false для отключения призыва к аккумулятору
    priority: 2,
    duration: 10000,
    maxPerSession: 1,
    delay: 0,
    color: 'warning'
  },
  NEW_BONUS: {
    id: 'new_bonus',
    enabled: true, // 🎁 Установите false для отключения уведомлений о бонусах
    priority: 1,
    duration: 12000,
    maxPerSession: 1,
    delay: 0,
    color: 'info'
  }
};

// Состояние системы уведомлений
class ToastNotificationSystem {
  constructor() {
    this.queue = [];
    this.activeToast = null;
    this.sessionCounts = {};
    this.userPreferences = this.loadUserPreferences();
    this.isInitialized = false;
  }

  // Загрузка пользовательских настроек
  loadUserPreferences() {
    const saved = safeLocalStorage.getItem(CONFIG.LOCAL_STORAGE_KEYS.PREFERENCES);
    const defaultEnabledTypes = Object.values(TOAST_TYPES).map((type) => type.id);
    const defaultPrefs = {
      enabled: true,
      enabledTypes: defaultEnabledTypes,
      lastSession: Date.now()
    };

    try {
      if (!saved) return defaultPrefs;

      const parsed = JSON.parse(saved);

      return {
        ...defaultPrefs,
        ...parsed,
        // Respect types the user switched off; fall back to defaults only if the list is missing
        enabledTypes: Array.isArray(parsed.enabledTypes) ? parsed.enabledTypes : defaultEnabledTypes
      };
    } catch (error) {
      if (CONFIG.IS_DEVELOPMENT) console.warn('Failed to parse toast preferences:', error);
      return defaultPrefs;
    }
  }

  // Сохранение настроек пользователя
  saveUserPreferences() {
    safeLocalStorage.setItem(
      CONFIG.LOCAL_STORAGE_KEYS.PREFERENCES, 
      JSON.stringify(this.userPreferences)
    );
  }

  // Перезагрузка preferences из localStorage (для синхронизации с NotificationSettings)
  reloadPreferences() {
    this.userPreferences = this.loadUserPreferences();
  }

  // Инициализация системы
  initialize() {
    if (this.isInitialized) return;
    
    this.isInitialized = true;
    this.resetSessionIfNeeded();
    
    if (CONFIG.IS_DEVELOPMENT) {
      console.log('🔔 Toast notification system initialized');
    }
  }

  // Сброс счетчиков сессии (если прошло больше 2 часов)
  resetSessionIfNeeded() {
    const now = Date.now();
    const resetThreshold = CONFIG.SESSION_RESET_HOURS * 60 * 60 * 1000;
    
    if (now - this.userPreferences.lastSession > resetThreshold) {
      this.sessionCounts = {};
      this.userPreferences.lastSession = now;
      this.saveUserPreferences();
    }
  }

  // Проверка возможности показа уведомления
  canShowToast(typeId) {
    if (!this.userPreferences.enabled) return false;
    if (!this.userPreferences.enabledTypes.includes(typeId)) return false;
    
    const type = TOAST_TYPES[typeId.toUpperCase()];
    if (!type) return false;
    
    // ✅ Проверяем флаг enabled для ручного отключения типов
    if (!type.enabled) {
      if (CONFIG.IS_DEVELOPMENT) {
        console.log(`🔕 Toast type '${typeId}' is manually disabled`);
      }
      return false;
    }
    
    const currentCount = this.sessionCounts[typeId] || 0;
    return currentCount < type.maxPerSession;
  }

  // Добавление уведомления в очередь
  addToQueue(toast) {
    if (!this.canShowToast(toast.type)) return;

    // Удаляем дубликаты того же типа
    this.queue = this.queue.filter(item => item.type !== toast.type);
    
    // Добавляем в очередь с сортировкой по приоритету
    this.queue.push(toast);
    this.queue.sort((a, b) => {
      const typeA = TOAST_TYPES[a.type.toUpperCase()];
      const typeB = TOAST_TYPES[b.type.toUpperCase()];
      return typeB.priority - typeA.priority;
    });

    this.processQueue();
  }

  // Обработка очереди уведомлений
  processQueue() {
    if (this.activeToast || this.queue.length === 0) return;

    const nextToast = this.queue.shift();
    this.showToast(nextToast);
  }

  // Показ уведомления
  showToast(toast) {
    this.activeToast = toast;
    this.incrementSessionCount(toast.type);

    // Уведомляем React компонент
    if (typeof window !== 'undefined' && window.mainBetToastEmitter) {
      window.mainBetToastEmitter.emit('show', toast);
    }

    // Автоматическое скрытие
    setTimeout(() => {
      this.hideToast();
    }, TOAST_TYPES[toast.type.toUpperCase()].duration);
  }

  // Скрытие активного уведомления
  hideToast() {
    if (!this.activeToast) return;

    if (typeof window !== 'undefined' && window.mainBetToastEmitter) {
      window.mainBetToastEmitter.emit('hide');
    }

    this.activeToast = null;
    
    // Небольшая задержка перед следующим уведомлением
    setTimeout(() => {
      this.processQueue();
    }, CONFIG.QUEUE_PROCESS_DELAY_MS);
  }

  // Увеличение счетчика сессии
  incrementSessionCount(typeId) {
    this.sessionCounts[typeId] = (this.sessionCounts[typeId] || 0) + 1;
  }

  // === КОНКРЕТНЫЕ УВЕДОМЛЕНИЯ ===

  // 🔥 Топ-матч дня (через 15 секунд)
  showTopMatchToast() {
    const allMatches = (typeof window !== 'undefined' && window.mainBetData?.todayMatches) || [];
    if (allMatches.length === 0) return;

    // Дополнительная проверка: фильтруем только матчи на сегодня
    // Local calendar day of the visitor, not UTC
    const today = new Date().toDateString();
    const todayMatches = allMatches.filter(match => {
      if (!match.matchDate) return false;
      return new Date(match.matchDate).toDateString() === today;
    });

    if (todayMatches.length === 0) {
      if (CONFIG.IS_DEVELOPMENT) {
        console.log(`🔕 No matches for today (${today}) found for top match toast`);
      }
      return;
    }

    if (CONFIG.IS_DEVELOPMENT) {
      console.log(`🔥 Found ${todayMatches.length} matches for today's top match selection`);
    }

    const topMatch = pickTopMatch(todayMatches);
    if (!topMatch) return;

    this.addToQueue({
      type: 'top_match',
      title: 'Spotlight match today',
      message: `${topMatch.homeTeam} vs ${topMatch.awayTeam}`,
      action: {
        text: 'See pick',
        url: `/${topMatch.slug}`
      }
    });
  }

  // 📈 Изменение коэффициентов
  showOddsChangeToast(match, oldOdds, newOdds) {
    const change = this.calculateOddsChange(oldOdds, newOdds);
    if (Math.abs(change) < 0.1) return; // Игнорируем мелкие изменения

    const changeText = change > 0 ? 'increased' : 'decreased';

    this.addToQueue({
      type: 'odds_change',
      direction: change > 0 ? 'up' : 'down',
      title: 'Odds update',
      message: `${match.homeTeam} vs ${match.awayTeam} odds ${changeText}`,
      action: {
        text: 'View match',
        url: `/${match.slug}`
      }
    });
  }

  // 💰 Призыв к аккумулятору
  showAccumulatorPrompt() {
    const viewedMatches = (typeof window !== 'undefined' && window.mainBetData?.viewedMatches) || [];
    if (viewedMatches.length < 2) return;

    this.addToQueue({
      type: 'accumulator_prompt',
      title: "Today's accas",
      message: 'Ready-made slips built from our picks.',
      action: {
        text: 'See accas',
        url: '/accumulators/'
      }
    });
  }

  // 🎁 Новые бонусы
  showNewBonusToast(bonus) {
    this.addToQueue({
      type: 'new_bonus',
      title: 'Welcome offer',
      message: `${bonus.title}. See terms and how to claim.`,
      action: {
        text: 'View bonus',
        url: `/bookie-bonuses/${bonus.slug}`
      }
    });
  }

  // === ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ ===

  calculateOddsChange(oldOdds, newOdds) {
    const oldAvg = (oldOdds.home + oldOdds.away + oldOdds.draw) / 3;
    const newAvg = (newOdds.home + newOdds.away + newOdds.draw) / 3;
    return newAvg - oldAvg;
  }

  // Отключение уведомлений пользователем
  disableNotifications() {
    this.userPreferences.enabled = false;
    this.saveUserPreferences();
    this.hideToast();
  }

  // Отключение конкретного типа уведомлений
  disableNotificationType(typeId) {
    this.userPreferences.enabledTypes = this.userPreferences.enabledTypes.filter(
      id => id !== typeId
    );
    this.saveUserPreferences();
  }
}

// Глобальный экземпляр системы
export const toastSystem = new ToastNotificationSystem();

// Простой event emitter для связи с React
class SimpleEventEmitter {
  constructor() {
    this.events = {};
  }

  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  emit(event, data) {
    if (this.events[event]) {
      this.events[event].forEach(callback => callback(data));
    }
  }

  off(event, callback) {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    }
  }
}

// Утилиты для быстрого управления типами уведомлений
export const toastControls = {
  // Отключить все типы
  disableAll: () => {
    Object.keys(TOAST_TYPES).forEach(key => {
      TOAST_TYPES[key].enabled = false;
    });
    if (CONFIG.IS_DEVELOPMENT) console.log('🔕 All toast types disabled');
  },
  
  // Включить все типы
  enableAll: () => {
    Object.keys(TOAST_TYPES).forEach(key => {
      TOAST_TYPES[key].enabled = true;
    });
    if (CONFIG.IS_DEVELOPMENT) console.log('🔔 All toast types enabled');
  },
  
  // Отключить конкретный тип
  disable: (type) => {
    const upperType = type.toUpperCase();
    if (TOAST_TYPES[upperType]) {
      TOAST_TYPES[upperType].enabled = false;
      if (CONFIG.IS_DEVELOPMENT) console.log(`🔕 Toast type '${type}' disabled`);
    }
  },
  
  // Включить конкретный тип
  enable: (type) => {
    const upperType = type.toUpperCase();
    if (TOAST_TYPES[upperType]) {
      TOAST_TYPES[upperType].enabled = true;
      if (CONFIG.IS_DEVELOPMENT) console.log(`🔔 Toast type '${type}' enabled`);
    }
  },
  
  // Показать статус всех типов
  status: () => {
    if (CONFIG.IS_DEVELOPMENT) {
      console.log('📊 Toast Types Status:');
      Object.entries(TOAST_TYPES).forEach(([key, config]) => {
        const status = config.enabled ? '✅' : '❌';
        console.log(`  ${status} ${key}: ${config.enabled ? 'enabled' : 'disabled'}`);
      });
    }
  }
};

// Глобальный emitter и контролы для разработки
if (typeof window !== 'undefined') {
  window.mainBetToastEmitter = new SimpleEventEmitter();
  
  // 🔔 Обработчик для ручного тестирования из консоли
  window.mainBetToastEmitter.on('showToast', (toastData) => {
    // Для тестирования - показываем тост напрямую, минуя проверки лимитов
    toastSystem.showToast(toastData);
  });
  
  // 🚀 Автоинициализация системы при загрузке
  toastSystem.initialize();
  
  // 🛠️ Глобальные контролы для консоли разработчика
  window.toastControls = toastControls;
  window.TOAST_TYPES = TOAST_TYPES;
}

// Экспорт основных функций
const toastNotifications = {
  initialize: () => toastSystem.initialize(),
  showTopMatch: () => toastSystem.showTopMatchToast(),
  showOddsChange: (match, oldOdds, newOdds) => toastSystem.showOddsChangeToast(match, oldOdds, newOdds),
  showAccumulatorPrompt: () => toastSystem.showAccumulatorPrompt(),
  showNewBonus: (bonus) => toastSystem.showNewBonusToast(bonus),
  disable: () => toastSystem.disableNotifications(),
  disableType: (typeId) => toastSystem.disableNotificationType(typeId),
  reloadPreferences: () => toastSystem.reloadPreferences(),
  hasActiveToast: () => !!toastSystem.activeToast,
  getQueueLength: () => toastSystem.queue.length,
  controls: toastControls
};

export default toastNotifications; 